import {
  Button,
  DateInput,
  ModalityCodesEnum,
  Select,
  SelectOptionType,
} from '@octano/global-ui';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';
import RemoteSearchSelect from '../../../../components/form/RemoteSearchSelect';
import useSelectOptions from '../../../../hooks/useSelectOptions';
import { useValidations } from '../../../../hooks/useValidations';
import { PeriodType } from '../../../../types/sectionMaintainerTypes';
import { ScheduleSectionFormData } from '../../../SectionsMaintainer/parts/ModalMaintainerSchedulePre';
import { NonStructuredSectionClassroomLoader } from '../hooks/NonStructuredSectionClassroomLoader';
import { useNonStructuredSearchResponsible } from '../hooks/useNonStructuredSearchResponsible';
import { NonStructuredSectionClassroomLoaderFilters } from '../types';
import MaintainerScheduleClassroomSelector from './MaintainerScheduleClassroomSelector';

const prefix = 'sectionsMaintainer.update.modalClass';

type Props = {
  sectionId: number | string;
  sectionScheduleId?: number | null;
  action: 'Update' | 'Create';
  activityOptions: SelectOptionType[];
  campusOptions: SelectOptionType[];
  moduleStartOptions: SelectOptionType[];
  moduleEndOptions: SelectOptionType[];
  responsibleOptions: SelectOptionType[];
  modalityOptions: SelectOptionType[];
  periodicityOptions: SelectOptionType[];
  onClose: () => void;
  period?: PeriodType;
};

const MaintainerScheduleFormFields = ({
  sectionId,
  sectionScheduleId,
  action,
  activityOptions,
  campusOptions,
  moduleStartOptions,
  moduleEndOptions,
  modalityOptions,
  periodicityOptions,
  onClose,
  period,
}: Props) => {
  const isUpdate = action === 'Update';

  const { t } = useTranslation();
  const { daysOptions } = useSelectOptions();
  const searchResponsible = useNonStructuredSearchResponsible();
  const {
    msgValidations,
    validateBeforeDate,
    validateTextNotEmpty,
  } = useValidations();

  const {
    control,
    watch,
    setValue,
    trigger,
  } = useFormContext<ScheduleSectionFormData>();

  const [
    startDate,
    endDate,
    startModule,
    endModule,
    day,
    selectedCampus,
    lessonsPeriodicity,
    modality,
  ] = watch([
    'startDate',
    'endDate',
    'startModule',
    'endModule',
    'day',
    'campus',
    'lessonsPeriodicity',
    'modality',
  ]);

  const filters = useMemo<NonStructuredSectionClassroomLoaderFilters>(() => {
    const getValue = <T extends any>(value: SelectOptionType | null) =>
      value?.value ? (value.value as T) : undefined;
    return {
      campusId: getValue(selectedCampus),
      day: getValue(day),
      startModuleId: getValue(startModule),
      endModuleId: getValue(endModule),
      startDate: startDate,
      endDate: endDate,
      sectionScheduleId: sectionScheduleId ?? undefined,
      lessonsPeriodicity: getValue(lessonsPeriodicity ?? null),
    };
  }, [
    day,
    endDate,
    selectedCampus,
    startModule,
    endModule,
    startDate,
    sectionScheduleId,
    lessonsPeriodicity,
  ]);

  const optional = (value: string) => {
    if (modality?.deliveryMode === ModalityCodesEnum.PR) {
      return validateTextNotEmpty(value);
    }
    return undefined;
  };

  // Al cambiar el modulo de término se vuelve a validar el módulo de inicio
  useEffect(() => {
    if (endModule) {
      trigger('startModule');
    }
  }, [endModule, trigger]);

  // Al cambiar la fecha de término se vuelve a validar la fecha de inicio
  useEffect(() => {
    if (endDate) {
      trigger('startDate');
    }
  }, [endDate, trigger]);

  // Al cambiar o borrar el campus seleccionado
  // se elimina la selección de sala
  const prevCampusRef = useRef(selectedCampus);
  useEffect(() => {
    const prevCampus = prevCampusRef.current;
    if (prevCampus?.value !== selectedCampus?.value) {
      setValue('classroom', null);
      prevCampusRef.current = selectedCampus;
    }
  }, [selectedCampus, setValue]);

  return (
    <>
      <Row className="pb-2">
        <Col xs={12} lg={4}>
          <Select
            label={t(`${prefix}.activity`)}
            name="activity"
            options={activityOptions}
            control={control}
            rules={{ required: msgValidations.required }}
            shouldUnregister
          />
        </Col>
        <Col xs={12} lg={4}>
          <Select
            label={t(`${prefix}.campus`)}
            name="campus"
            options={campusOptions}
            control={control}
            rules={{ validate: { require: optional } }}
            shouldUnregister
            disabled={modality?.deliveryMode === ModalityCodesEnum.OL}
          />
        </Col>
        <Col xs={12} lg={4}>
          <Select
            label={t(`${prefix}.modality`)}
            name="modality"
            options={modalityOptions}
            control={control}
            shouldUnregister
          />
        </Col>
        <Col xs={12} lg={4}>
          <Select
            label={t(`${prefix}.periodicity`)}
            name="lessonsPeriodicity"
            options={periodicityOptions}
            control={control}
            shouldUnregister
          />
        </Col>
        <Col xs={12} lg={4}>
          <RemoteSearchSelect
            name="responsible"
            label={t(`${prefix}.responsible`)}
            filterOptions={searchResponsible}
            control={control}
            rules={{ required: msgValidations.required }}
            shouldUnregister
          />
        </Col>
        <Col xs={12} lg={4}>
          <Select
            label={t(`${prefix}.day`)}
            name="day"
            options={daysOptions}
            control={control}
            rules={{
              required: msgValidations.required,
            }}
            shouldUnregister
            disabled={isUpdate}
          />
        </Col>
        <Col xs={12} lg={4}>
          <Select
            label={t(`${prefix}.startModule`)}
            name="startModule"
            options={moduleStartOptions}
            control={control}
            rules={{
              required: msgValidations.required,
              validate: (value: SelectOptionType) => {
                if (value && endModule) {
                  const indexStart = moduleStartOptions.findIndex(
                    (mod) => mod.value === value.value,
                  );
                  const indexEnd = moduleStartOptions.findIndex(
                    (mod) => mod.value === endModule.value,
                  );
                  if (
                    indexStart >= 0 &&
                    indexEnd >= 0 &&
                    indexStart > indexEnd
                  ) {
                    return msgValidations.inValidBeforeModule;
                  }
                }
                return undefined;
              },
            }}
            shouldUnregister
          />
        </Col>
        <Col xs={12} lg={4}>
          <Select
            label={t(`${prefix}.endModule`)}
            name="endModule"
            options={moduleEndOptions}
            control={control}
            rules={{ required: msgValidations.required }}
            shouldUnregister
          />
        </Col>

        <Col xs={12} lg={4}>
          <DateInput
            label={t(`${prefix}.startDate`)}
            name="startDate"
            control={control}
            rules={{
              required: msgValidations.required,
              validate: validateBeforeDate(endDate),
            }}
            minDate={
              period?.startDate ? dayjs(period.startDate).toDate() : undefined
            }
            maxDate={
              period?.endDate ? dayjs(period.endDate).toDate() : undefined
            }
            shouldUnregister
            disabled={isUpdate}
          />
        </Col>
        <Col xs={12} lg={4}>
          <DateInput
            label={t(`${prefix}.endDate`)}
            name="endDate"
            control={control}
            rules={{ required: msgValidations.required }}
            minDate={
              period?.startDate ? dayjs(period.startDate).toDate() : undefined
            }
            maxDate={
              period?.endDate ? dayjs(period.endDate).toDate() : undefined
            }
            shouldUnregister
            disabled={isUpdate}
          />
        </Col>

        {/* selector de salas disponible solo en modalidad presencial */}
        {modality?.deliveryMode === ModalityCodesEnum.PR ? (
          <Col xs={12} lg={4}>
            <NonStructuredSectionClassroomLoader
              defaultQuery={{ ...filters, sectionId }}
            >
              <MaintainerScheduleClassroomSelector
                sectionId={sectionId}
                filters={filters}
              />
            </NonStructuredSectionClassroomLoader>
          </Col>
        ) : null}

        <Col xs={12} className="pb-2 text-center">
          {watch('campus') &&
            !watch('classroom') &&
            modality?.deliveryMode === ModalityCodesEnum.PR && (
              <p className="text-danger">{t(`${prefix}.obs`)}</p>
            )}
        </Col>
        <Col xs={12}>
          <Row>
            <Col xs={12} lg={4} className="pb-2 pb-lg-0 ml-auto">
              <Button
                text={t('common.actions.close')}
                outlined
                onClick={() => {
                  onClose();
                }}
                fullwidth
              />
            </Col>
            <Col xs={12} lg={4}>
              <Button
                type="submit"
                text={t('common.actions.confirm')}
                fullwidth
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default MaintainerScheduleFormFields;
